import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { apiClient } from "@/shared/api/client"
import type { Post, PostsQueryParams } from "@/shared/lib/types"

const POSTS_PER_PAGE = 30

export const fetchPosts = async (params?: PostsQueryParams): Promise<Post[]> => {
	const { data } = await apiClient.get<Post[]>("/posts", { params })
	return data
}

export const fetchPost = async (id: string): Promise<Post> => {
	const { data } = await apiClient.get<Post>(`/posts/${id}`)
	return data
}

export const usePosts = (params?: PostsQueryParams) => {
	return useQuery({
		queryKey: ["posts", params],
		queryFn: () => fetchPosts(params),
	})
}

export const useInfinitePosts = (
	params?: Omit<PostsQueryParams, "limit" | "offset">,
	options?: { enabled?: boolean },
) => {
	return useInfiniteQuery({
		queryKey: ["posts", "infinite", params],
		queryFn: ({ pageParam = 0 }) =>
			fetchPosts({
				...params,
				limit: POSTS_PER_PAGE,
				offset: pageParam,
			}),
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage.length < POSTS_PER_PAGE) {
				return undefined
			}
			return allPages.length * POSTS_PER_PAGE
		},
		initialPageParam: 0,
		enabled: options?.enabled ?? true,
	})
}

export const usePost = (id: string) => {
	return useQuery({
		queryKey: ["post", id],
		queryFn: () => fetchPost(id),
		enabled: !!id,
		staleTime: 0,
	})
}
